import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useMatch } from 'react-router-dom'

export const useIsMountedRef = () => {
  const ref = useRef(true)

  useEffect(() => {
    return () => {
      ref.current = false
    }
  }, [])

  return ref
}

export const useSiblingLocation = (route, search) => {
  const location = useLocation()
  const { url } = useMatch()
  const parentUrl = getParentPath(url)

  const nextLocation = useMemo(() => {
    return {
      pathname: parentUrl + route,
      search: getSearchString(location.search, search)
    }
  }, [location.search, parentUrl, route, search])

  return nextLocation
}

export const useNextLocationInPopup = nextPathname => {
  const location = useLocation()

  const nextLocation = useMemo(() => {
    const search = getSearchString(location.search, { goback: location.pathname })
    return { search, pathname: nextPathname }
  }, [location.pathname, location.search, nextPathname])

  return nextLocation
}

const getParentPath = path => {
  return path.substr(0, path.lastIndexOf('/'))
}

// TODO: this is not a hook
export const getSearchString = (search, nextSearch = {}) => {
  const searchParams = new URLSearchParams(search)

  Object.entries(nextSearch).forEach(([key, value]) => {
    searchParams.set(key, value)
  })

  return searchParams.toString()
}

export const useInterval = (callback, delay) => {
  const intervalRef = useRef(null)
  const callbackRef = useRef(callback)
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (delay !== null) {
      intervalRef.current = window.setInterval(() => callbackRef.current(), delay)
      return () => window.clearInterval(intervalRef.current || 0)
    }
  }, [delay])

  return intervalRef
}
// export const useLocationSearch = <T extends object>(): T => {
//   const location = useLocation()
//   const searchParams = new URLSearchParams()
//   const searchParamsKeys = [...searchParams.keys()]
//   const out = {}

//   searchParamsKeys.forEach(key => {
//     out[key] = searchParams.get(key)
//   })

//   return searchParams
// }

// TODO: fix any
export const usePromise = fn => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState()
  const [error, setError] = useState(null)
  // const [onError, setOnError] = useState<((error: Error) => void) | null>(null)
  // const [onData, setOnData] = useState<((data: T) => void) | null>(null)
  // const [call, setCall] = useState<(() => void) | null>(null)
  const isMountedRef = useRef(false)

  const onPromiseSuccess = useCallback(data => {
    if (isMountedRef.current) {
      setData(data)
    }
  }, [])

  const onPromiseError = useCallback(error => {
    if (isMountedRef.current) {
      // @ts-ignore
      setError(error)
    }
  }, [])

  const onPromiseFinally = useCallback(() => {
    if (isMountedRef.current) {
      setLoading(false)
    }
  }, [])

  // TODO: fix any
  const call = useCallback(
    (...args) => {
      const promise = fn(...args)
      setLoading(true)
      promise.then(onPromiseSuccess).catch(onPromiseError).finally(onPromiseFinally)
    },
    [fn, onPromiseError, onPromiseFinally, onPromiseSuccess]
  )

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  return { call, data, error, loading }
}

export const useIsCurrentPath = path => {
  const location = useLocation()
  const [isCurrent, setIsCurrent] = useState(false)

  useEffect(() => {
    setIsCurrent(location.pathname === path)
  }, [location, path])

  return isCurrent
}

export const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = event => {
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }
      handler(event)
    }
    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [ref, handler])
}

export function useMediaQuery(query) {
  const supportsMatchMedia = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  const [matches, setMatches] = useState(() => (supportsMatchMedia ? window.matchMedia(query).matches : false))

  useEffect(() => {
    if (!supportsMatchMedia) {
      return () => {}
    }

    const mq = window.matchMedia(query)
    const onChange = evt => {
      setMatches(evt.matches)
    }

    mq.addListener(onChange)
    return () => {
      mq.removeListener(onChange)
    }
  }, [supportsMatchMedia, query])

  // If in SSR, the media query should never match. Once the page hydrates,
  // this will update and the real value will be returned.
  return matches
}

export function useIsLargeDesktop() {
  return useMediaQuery('(min-width: 1200px)')
}

export function useIsSmallDesktop() {
  return useMediaQuery('(min-width: 992px) and (max-width: 1200px)')
}
